import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { BookOpenCheck, ChevronDown, Loader2, RotateCcw, Send, CheckCircle } from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchBankDetailsWithAvailableBalance, selectBankDetailsArray, selectBankDetailsLoading,
} from '../../slices/CommonSlice/bankDetailsSlice';

import {
    submitChequeBook, clearSaveResult, resetChequeBookCreation, selectChequeBookCreation,
} from '../../slices/accountsSlice/chequeBookCreationSlice';

// ── Shared primitives ────────────────────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';
const selectCls = inputCls + ' appearance-none pr-9 cursor-pointer';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const onlyDigits = (v) => v.replace(/\D/g, '').slice(0, 6);

const ChequeBookCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const currentUser = userData?.userName || userData?.userId || userData?.UID || userData?.uid || '';

    const banks = useSelector(selectBankDetailsArray);
    const banksLoading = useSelector(selectBankDetailsLoading);
    const { saveLoading, saveResult, saveError } = useSelector(selectChequeBookCreation);

    const [bankId, setBankId] = useState('');
    const [openingDate, setOpeningDate] = useState(new Date());
    const [firstChequeNo, setFirstChequeNo] = useState('');
    const [lastChequeNo, setLastChequeNo] = useState('');
    const [remarks, setRemarks] = useState('');
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        dispatch(fetchBankDetailsWithAvailableBalance());
        return () => { dispatch(resetChequeBookCreation()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (saveResult) {
            toast.success('Cheque numbers added successfully.');
            dispatch(clearSaveResult());
            setSubmitted(true);
        }
        if (saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError]);

    const handleReset = () => {
        setBankId('');
        setOpeningDate(new Date());
        setFirstChequeNo('');
        setLastChequeNo('');
        setRemarks('');
        setSubmitted(false);
    };

    const handleSubmit = () => {
        if (!bankId) { toast.warning('Select Bank Name.'); return; }
        if (!(openingDate instanceof Date)) { toast.warning('Select date.'); return; }
        if (!firstChequeNo) { toast.warning('Enter First Cheque Number.'); return; }
        if (!lastChequeNo) { toast.warning('Enter Last Cheque Number.'); return; }
        if (firstChequeNo.length !== 6 || lastChequeNo.length !== 6) { toast.warning('Please enter six digits in from and to cheque numbers.'); return; }
        if (Number(firstChequeNo) >= Number(lastChequeNo)) { toast.warning('Please enter valid cheque numbers.'); return; }
        if (!remarks.trim()) { toast.warning('Please enter remarks.'); return; }

        dispatch(submitChequeBook({
            BankName: bankId,
            // Local calendar date — toISOString() is UTC and lands on the previous day in IST.
            ChequeOpeningDate: `${openingDate.getFullYear()}-${String(openingDate.getMonth() + 1).padStart(2, '0')}-${String(openingDate.getDate()).padStart(2, '0')}`,
            FirstChequeno: firstChequeNo,
            LastChequeno: lastChequeNo,
            Remarks: remarks.trim(),
            CreatedBy: currentUser,
            RoleID: parseInt(roleId, 10) || 0,
        }));
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><BookOpenCheck className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">{menuData?.name || 'Cheque Book Creation'}</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">Register a new cheque number range for a bank</p>
                    </div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 py-6">
                <Card className="p-6 md:p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label required>Bank Name</Label>
                            <SelectWrap loading={banksLoading}>
                                <select className={selectCls} value={bankId} onChange={(e) => setBankId(e.target.value)} disabled={saveLoading || submitted}>
                                    <option value="">-Please Select-</option>
                                    {banks.map((b) => <option key={b.BankId} value={b.BankId}>{b.BankName}</option>)}
                                </select>
                            </SelectWrap>
                        </div>
                        <div>
                            <Label required>Date</Label>
                            <CustomDatePicker value={openingDate} onChange={setOpeningDate} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} disabled={saveLoading || submitted} />
                        </div>
                        <div>
                            <Label required>First Cheque No</Label>
                            <input
                                className={inputCls} value={firstChequeNo}
                                onChange={(e) => setFirstChequeNo(onlyDigits(e.target.value))}
                                disabled={saveLoading || submitted} inputMode="numeric" maxLength={6}
                            />
                        </div>
                        <div>
                            <Label required>Last Cheque No</Label>
                            <input
                                className={inputCls} value={lastChequeNo}
                                onChange={(e) => setLastChequeNo(onlyDigits(e.target.value))}
                                disabled={saveLoading || submitted} inputMode="numeric" maxLength={6}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <Label required>Remarks</Label>
                            <textarea rows={3} className={inputCls + ' resize-none'} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={saveLoading || submitted} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button type="button" onClick={handleReset}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all">
                            <RotateCcw className="h-3.5 w-3.5" /> Reset
                        </button>
                        <button type="button" onClick={handleSubmit} disabled={saveLoading || submitted}
                            className="flex items-center gap-2 px-6 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold shadow-md hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                            {saveLoading
                                ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                                : submitted
                                    ? <><CheckCircle className="h-4 w-4" /> Submitted</>
                                    : <><Send className="h-4 w-4" /> Submit</>}
                        </button>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default ChequeBookCreation;
